import { BACKEND_URL } from "../../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Invoices / Documents (thread #1388, 2026-09-10) — RTK Query API.
// BFF enforces TABC gates server-side on every call; 403 if not authorized.
// All reads hit airtable_mirror (no live Airtable — TL Test 2 PASS).
// Upload path: presign → GCS direct-upload → /portal/upload (record create).
export const invoicesDocumentsApi = createApi({
  reducerPath: "invoicesDocumentsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["Invoice"],
  endpoints: (builder) => ({
    // Read page — location-filtered list.
    listInvoices: builder.query({
      query: () => `/invoices-documents/portal/list`,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Invoice", id })),
              { type: "Invoice", id: "LIST" },
            ]
          : [{ type: "Invoice", id: "LIST" }],
    }),

    // Upload form — Companies dropdown.
    getEntityOptions: builder.query({
      query: () => `/invoices-documents/portal/meta/entities`,
    }),

    // Upload form — Employees dropdown.
    getEmployeeOptions: builder.query({
      query: () => `/invoices-documents/portal/meta/employees`,
    }),

    // Upload form — mint GCS presigned POST policy.
    presignUpload: builder.mutation({
      query: ({ filename, contentType }) => ({
        url: `/invoices-documents/portal/presign?filename=${encodeURIComponent(filename)}&contentType=${encodeURIComponent(contentType)}`,
        method: "GET",
      }),
    }),

    // Upload form — create Airtable record + mirror row after GCS upload.
    uploadDocument: builder.mutation({
      query: (body) => ({
        url: `/invoices-documents/portal/upload`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Invoice", id: "LIST" }],
    }),

    // Admin page — all records, no location filter (President only).
    adminListInvoices: builder.query({
      query: () => `/invoices-documents/portal/admin/list`,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Invoice", id })),
              { type: "Invoice", id: "ADMIN_LIST" },
            ]
          : [{ type: "Invoice", id: "ADMIN_LIST" }],
    }),

    // Admin page — inline edit.
    adminUpdateInvoice: builder.mutation({
      query: ({ recid, ...body }) => ({
        url: `/invoices-documents/portal/admin/update/${recid}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { recid }) => [
        { type: "Invoice", id: recid },
        { type: "Invoice", id: "ADMIN_LIST" },
      ],
    }),

    // Admin page — Companies dropdown for Company (entity) inline edit.
    getAdminEntityOptions: builder.query({
      query: () => `/invoices-documents/portal/admin/meta/entities`,
    }),

    // Admin page — migrate expired Airtable CDN links to permanent GCS URLs.
    migrateAttachments: builder.mutation({
      query: () => ({
        url: `/invoices-documents/portal/admin/migrate-attachments`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Invoice", id: "ADMIN_LIST" }, { type: "Invoice", id: "LIST" }],
    }),
  }),
});

export const {
  useListInvoicesQuery,
  useGetEntityOptionsQuery,
  useGetEmployeeOptionsQuery,
  usePresignUploadMutation,
  useUploadDocumentMutation,
  useAdminListInvoicesQuery,
  useAdminUpdateInvoiceMutation,
  useGetAdminEntityOptionsQuery,
  useMigrateAttachmentsMutation,
} = invoicesDocumentsApi;
